import { ThemeProvider } from "@app/ui";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { App } from "./App.js";
import { installGlobalErrorHandlers } from "./global-error-handlers.js";
import { hydrateAppStores } from "./lib/boot-hydrate.js";
import { initStorage } from "./platform.js";
import { router } from "./router.js";
import { registerServerWakingToast } from "./lib/server-waking.js";
import { registerServiceWorker } from "./sw-register.js";
import "./styles/globals.css";

async function bootstrap() {
  // Ghost-demo iframe boot (`?demo=<script-id>`): ephemeral storage + a single
  // scripted view instead of the full app. Lazy chunk — free on normal boots.
  if (new URLSearchParams(window.location.search).has("demo")) {
    const { bootDemo } = await import("./features/demos/boot.js");
    return bootDemo(window.location.search);
  }
  await initStorage();
  // Entity stores before the first render, not from a mount effect: see
  // `hydrateAppStores`. Landing them after the first data render costs the
  // expensive routes a whole second render/commit.
  await hydrateAppStores();
  installGlobalErrorHandlers();
  registerServerWakingToast();
  registerServiceWorker();
  const root = document.getElementById("root");
  if (!root) throw new Error("#root missing");
  createRoot(root).render(
    <StrictMode>
      <ThemeProvider>
        <App>
          <RouterProvider router={router} />
        </App>
      </ThemeProvider>
    </StrictMode>,
  );
}

// A boot failure must never be a silent white screen (no devtools on a phone):
// paint the error where the app would have been.
void bootstrap().catch((e: unknown) => {
  console.error("bootstrap failed", e);
  const root = document.getElementById("root");
  if (!root) return;
  const msg = e instanceof Error ? `${e.message}\n\n${e.stack ?? ""}` : String(e);
  root.innerHTML = "";
  const box = document.createElement("pre");
  box.style.cssText = "padding:16px;white-space:pre-wrap;word-break:break-word;font-family:var(--app-font-family);font-size:calc(13px * var(--app-text-scale));line-height:1.5;color:#b91c1c";
  box.textContent = `The app failed to start.\n\n${msg}`;
  root.appendChild(box);
});
